import { squareApiService } from "./squareApiService";
import { readSquareAttempt, removeSquareAttempt, type SquareAttempt, type SquareResult } from "./squareBrowserFlow";
import { fromSquareConnectionDtoToSquareConnection } from "../types/squareTypes";
import { isSquareConflict } from "./squareErrors";

export type SquareCallbackOutcome = { result: SquareResult; conflict?: boolean };

type Callback = {
  attemptId: string;
  businessId: string;
  locationId: string;
  result: string;
  receipt: string;
  completionAttempt?: SquareAttempt;
  completion?: Promise<SquareCallbackOutcome>;
};
let captured: Callback | undefined;

export function discardSquareCallback(callback: Callback) {
  callback.receipt = "";
  removeSquareAttempt(callback.attemptId);
}

export function captureSquareCallback(): Callback {
  // Retain only in this document's memory: StrictMode may initialize the page twice.
  if (!captured || window.location.hash) {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    window.history.replaceState(window.history.state, "", window.location.pathname);
    captured = {
      attemptId: fragment.get("attempt_id") ?? "",
      businessId: fragment.get("business_id") ?? "",
      locationId: fragment.get("location_id") ?? "",
      result: fragment.get("result") ?? "",
      receipt: fragment.get("completion_token") ?? "",
    };
  }
  return captured;
}

export function matchSquareCallback(callback: Callback, userId: string): SquareAttempt | null {
  const attempt = callback.completionAttempt ?? readSquareAttempt(callback.attemptId);
  return attempt && attempt.userId === userId && attempt.businessId === callback.businessId &&
    attempt.locationId === callback.locationId ? attempt : null;
}

export function completeSquareCallback(callback: Callback, attempt: SquareAttempt): Promise<SquareCallbackOutcome> {
  // The promise survives effect replays; a one-use receipt must never be submitted twice.
  if (!callback.completion) {
    callback.completionAttempt = attempt;
    callback.completion = (async (): Promise<SquareCallbackOutcome> => {
      try {
        if (callback.result === "cancelled") return { result: "cancelled" };
        if (callback.result !== "authorized" || !callback.receipt) return { result: "error" };
        const connection = fromSquareConnectionDtoToSquareConnection(await squareApiService.complete(attempt.businessId, {
          attempt_id: attempt.attemptId,
          browser_verifier: attempt.browserVerifier,
          completion_token: callback.receipt,
        }));
        return { result: connection.status === "connected" ? "connected" : "error" };
      } catch (error) {
        return { result: "error", conflict: isSquareConflict(error) };
      } finally {
        callback.receipt = "";
        removeSquareAttempt(attempt.attemptId);
      }
    })();
  }
  return callback.completion;
}
